"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { LinkButton } from "@offstreetparking/design-system/forms";
import { Link } from "@offstreetparking/design-system/typography";
import { routes } from "../../routes/routes";
import type { OverrideNotices } from "../CreatePermitWizard/types";

type OverrideNoticeAlertProps = {
	notices: OverrideNotices;
	onSelectPermitType?: (permitTypeId: number) => void;
};

export const OverrideNoticeAlert = ({ notices = [], onSelectPermitType }: OverrideNoticeAlertProps) => {
	const { t } = useLingui();
	const [firstNotice] = notices;
	if (firstNotice === undefined) {
		return null;
	}
	type NoticeAction = { permitTypeId: number; title: string } & (
		| { kind: "select" }
		| { kind: "link"; lookupId: string; ref?: string }
	);
	const actions = notices.flatMap((notice): NoticeAction[] => {
		const base = { permitTypeId: notice.overridingPermitTypeId, title: notice.overridingPermitTypeTitle };
		if (notice.tenantIsOnOverridingPermitType && onSelectPermitType !== undefined) {
			return [{ ...base, kind: "select" }];
		}
		if (notice.lookupId !== undefined) {
			return [{ ...base, kind: "link", lookupId: notice.lookupId, ref: notice.referrerLookupId }];
		}
		return [];
	});
	return (
		<Alert
			type="warning"
			title={
				<span className="font-normal text-base text-neutral">
					{notices.length === 1 ? (
						<Trans>
							Some options may be unavailable due to{" "}
							<span className="font-semibold">{firstNotice.overridingPermitTypeTitle}</span>
						</Trans>
					) : (
						<Trans>Some options may be unavailable due to active permit type overrides</Trans>
					)}
				</span>
			}
			message={
				actions.length > 0 ? (
					<div className="flex flex-wrap gap-x-4 gap-y-1">
						{actions.map((action) => {
							const label = t({
								comment:
									"Action in the override notice banner that takes the parker to the overriding permit type's registration; the placeholder is that permit type's name",
								message: `Park at ${action.title}`,
							});
							return action.kind === "select" ? (
								<LinkButton
									key={action.permitTypeId}
									type="button"
									className="font-semibold text-base"
									onClick={() => onSelectPermitType?.(action.permitTypeId)}
								>
									{label}
								</LinkButton>
							) : (
								<Link
									key={action.permitTypeId}
									href={routes.locationLookup({ lookup: action.lookupId, ref: action.ref })}
									className="font-semibold text-base"
								>
									{label}
								</Link>
							);
						})}
					</div>
				) : undefined
			}
		/>
	);
};
